import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { RefreshCw, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { checkForNewBuild, UPDATE_CHECK_INTERVAL_MS, storeUpdateAvailable, storeUrl, isStoreUpdateSnoozed, snoozeStoreUpdate } from "@/lib/appUpdate";
import { getAppVersion } from "@/lib/appVersion";
import { openExternal } from "@/lib/externalLinks";
import { markIntentionalReload } from "@/lib/crashReport";

const Card = ({ testId, title, hint, actionLabel, onAction, onClose, closeLabel, closeText, closeTestId }) => (
  <div className="fixed left-0 right-0 z-[65] px-3 pointer-events-none" style={{ top: "calc(var(--sa-top, 0px) + 0.75rem)" }} data-testid={testId}>
    <div className="pointer-events-auto mx-auto max-w-md rounded-2xl shadow-lg border px-3 py-2.5 flex items-center gap-3" style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }}>
      <img src="/logo192.png" alt="" className="h-9 w-9 rounded-xl shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" data-testid={`${testId}-title`}>{title}</div>
        <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{hint}</div>
      </div>
      <button type="button" onClick={onAction} className="shrink-0 rounded-full px-3 py-1.5 text-xs font-bold text-white flex items-center gap-1" style={{ background: "var(--yalola-primary)" }} data-testid={`${testId}-action`}>
        <RefreshCw size={13} /> {actionLabel}
      </button>
      <button type="button" onClick={onClose} className="shrink-0 h-8 min-w-8 px-1.5 grid place-items-center rounded-full text-xs font-medium" style={{ color: "var(--yalola-text-muted)" }} aria-label={closeLabel} data-testid={closeTestId || `${testId}-dismiss`}>
        {closeText || <X size={16} />}
      </button>
    </div>
  </div>
);

// Web / PWA : une fenêtre restée ouverte pendant un déploiement continuerait sinon avec l'ancien code.
// Vérification au montage, au retour au premier plan et toutes les 5 minutes.
const WebUpdateBanner = () => {
  const { t } = useApp();
  const [available, setAvailable] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let alive = true;
    const run = async () => { if (alive && !available && (await checkForNewBuild())) setAvailable(true); };
    run();
    const iv = setInterval(run, UPDATE_CHECK_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") run(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => { alive = false; clearInterval(iv); document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("focus", onVisible); };
  }, [available]);

  if (!available || hidden) return null;
  return (
    <Card testId="update-banner" title={t("app.update_available", "Nouvelle version disponible")}
      hint={t("app.update_hint", "Recharge pour profiter des dernières corrections.")}
      actionLabel={t("app.update_now", "Mettre à jour")} onAction={() => { markIntentionalReload("update"); window.location.reload(); }}
      onClose={() => setHidden(true)} closeLabel={t("common.close", "Fermer")} />
  );
};

// App native (Android / iOS) : la version « store » déclarée dans Admin › Utilisateurs (settings.app_release)
// est plus récente que le binaire installé → bandeau doux, jamais bloquant : « Mettre à jour » ouvre la fiche
// du store, « Plus tard » le masque 2 jours. Masqué pendant un direct (/live…) pour ne pas gêner.
const StoreUpdateBanner = () => {
  const { t, config } = useApp();
  const { pathname } = useLocation();
  const [update, setUpdate] = useState(null);
  const [hidden, setHidden] = useState(false);
  const platform = Capacitor.getPlatform();
  const release = config?.settings?.app_release;

  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!release) return;
      const v = await getAppVersion();
      const u = storeUpdateAvailable(release, platform, v.build);
      if (!alive) return;
      setUpdate(u);
      setHidden(u ? isStoreUpdateSnoozed(u.build) : false);
    };
    run();
    const onVisible = () => { if (document.visibilityState === "visible") run(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; document.removeEventListener("visibilitychange", onVisible); };
  }, [release, platform]);

  if (!update) return null;
  const open = () => { openExternal(storeUrl(config?.settings, platform)).catch(() => {}); };
  // Lot 574 : mise à jour OBLIGATOIRE (interrupteur admin) → pas de « Plus tard », même pendant un direct.
  // Lot 621 : popup non fermable (fond assombri qui bloque les touches) plutôt qu'un écran plein : le membre voit
  // toujours son application derrière et comprend qu'il s'agit d'une mise à jour, pas d'une panne.
  if (release?.[platform]?.mandatory) {
    return (
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-6" style={{ background: "rgba(20, 16, 12, 0.62)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)" }} role="dialog" aria-modal="true" data-testid="store-update-required" data-layout="popup">
        <div className="w-full max-w-sm text-center space-y-4 rounded-3xl border shadow-2xl px-6 py-7" style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }} data-testid="store-update-required-popup">
          <img src="/logo192.png" alt="" className="h-16 w-16 rounded-2xl mx-auto shadow-md" />
          <h1 className="text-xl font-bold" data-testid="store-update-required-title">{t("app.store_update_required_title", "Mise à jour obligatoire")}</h1>
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="store-update-required-text">
            {t("app.store_update_required_text", "Cette version de Yalola n'est plus prise en charge. Installe la version {version} depuis le store pour continuer : ça prend moins d'une minute.").replace("{version}", update.version)}
          </p>
          <button type="button" onClick={open} className="w-full rounded-full py-3 text-sm font-bold text-white flex items-center justify-center gap-2" style={{ background: "var(--yalola-primary)" }} data-testid="store-update-required-action">
            <RefreshCw size={16} /> {t("app.update_now", "Mettre à jour")}
          </button>
          <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("app.store_update_required_hint", "Après l'installation, rouvre simplement Yalola.")}</p>
        </div>
      </div>
    );
  }
  if (hidden || pathname === "/live" || pathname.startsWith("/live/")) return null;
  const later = () => { snoozeStoreUpdate(update.build); setHidden(true); };
  return (
    <Card testId="store-update-banner"
      title={t("app.store_update_title", "Nouvelle version de Yalola disponible ({version})").replace("{version}", update.version)}
      hint={t("app.store_update_hint", "Mets à jour pour profiter des dernières améliorations.")}
      actionLabel={t("app.update_now", "Mettre à jour")} onAction={open}
      onClose={later} closeLabel={t("app.update_later", "Plus tard")} closeText={t("app.update_later", "Plus tard")} closeTestId="store-update-banner-later" />
  );
};

export const UpdateBanner = () => (Capacitor.isNativePlatform() ? <StoreUpdateBanner /> : <WebUpdateBanner />);
